/**
 * PATH: apps/qa/src/components/camera-capture/CameraCapture.tsx
 * PURPOSE: Mobile-first camera capture with GPS, timestamp, voice notes, and offline queue
 * WHY: Field workers need to capture evidence on job sites with geolocation proof
 * FLOW: Open → Request permissions → Capture → Add notes → Queue → Sync when online
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { CameraOff, X, Image } from 'lucide-react'
import api from '@/lib/api'
import {
  assetUploadForm,
  photoEvidenceBody,
  QA_EVIDENCE_PATH,
  QDRIVE_UPLOAD_PATH,
} from '@/lib/evidence/evidencePayload'
import PhotoAnnotation from '../PhotoAnnotation'
import type { CameraCaptureProps, CapturedEvidence } from './types'
import { useGeolocation } from './useGeolocation'
import { useAudioRecording } from './useAudioRecording'
import { addToOfflineQueue } from './offlineQueue'
import { LocationIndicator } from './LocationIndicator'
import { OnlineIndicator } from './OnlineIndicator'
import { PreviewControls } from './PreviewControls'

export default function CameraCapture({
  attachTo, inspectionId, onCapture, onClose, multiCapture = false,
}: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const [stream, setStream] = useState<MediaStream | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null)
  const [notes, setNotes] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [captureCount, setCaptureCount] = useState(0)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isAnnotating, setIsAnnotating] = useState(false)

  const geo = useGeolocation()
  const audio = useAudioRecording()

  // Track online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Initialize camera
  useEffect(() => {
    async function initCamera() {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        })
        setStream(mediaStream)
        if (videoRef.current) videoRef.current.srcObject = mediaStream
      } catch (err: unknown) {
        if (import.meta.env.DEV) console.error('Camera error:', err)
        setCameraError(
          (err as { name?: string }).name === 'NotAllowedError'
            ? 'Camera access denied. Please enable camera permissions.'
            : 'Unable to access camera. Please check device settings.'
        )
      }
    }
    initCamera()
    return () => { stream?.getTracks().forEach(t => t.stop()) }
  }, [])

  // Capture photo with GPS/timestamp watermark
  const handleCapture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return
    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    ctx.drawImage(video, 0, 0)

    // Timestamp watermark
    const timestamp = new Date().toLocaleString()
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    ctx.fillRect(10, canvas.height - 40, 220, 30)
    ctx.fillStyle = '#ffffff'
    ctx.font = '14px monospace'
    ctx.fillText(timestamp, 20, canvas.height - 18)

    // GPS watermark
    if (geo.latitude && geo.longitude) {
      const gpsText = `${geo.latitude.toFixed(6)}, ${geo.longitude.toFixed(6)}`
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
      ctx.fillRect(10, canvas.height - 75, 250, 30)
      ctx.fillStyle = '#ffffff'
      ctx.fillText(`📍 ${gpsText}`, 20, canvas.height - 53)
    }

    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedImage(canvas.toDataURL('image/jpeg', 0.9))
        setCapturedBlob(blob)
      }
    }, 'image/jpeg', 0.9)
  }, [geo])

  const handleRetake = useCallback(() => {
    setCapturedImage(null)
    setCapturedBlob(null)
    setNotes('')
    audio.clearRecording()
  }, [audio])

  // Save/Upload evidence
  const handleSave = useCallback(async () => {
    if (!capturedBlob) return
    const evidenceId = `evidence_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const evidence: CapturedEvidence = {
      id: evidenceId, imageBlob: capturedBlob, imageDataUrl: capturedImage!,
      timestamp: new Date(),
      gps: { latitude: geo.latitude, longitude: geo.longitude, accuracy: geo.accuracy },
      voiceNoteBlob: audio.audioBlob || undefined,
      notes: notes || undefined, isPending: true,
    }

    if (isOnline) {
      setIsUploading(true)
      try {
        // Two legs: the bytes land in QDrive, then the returned asset id is
        // linked as evidence. A single multipart POST to /qa/evidence/ looked
        // like it worked and stored a row with no image.
        const fileName = `evidence_${Date.now()}.jpg`
        const upload = await api().post(
          QDRIVE_UPLOAD_PATH,
          assetUploadForm(capturedBlob, fileName),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        const assetId = upload.data?.id
        if (!assetId) throw new Error('Upload returned no asset id')

        await api().post(
          QA_EVIDENCE_PATH,
          photoEvidenceBody({
            target: { type: attachTo?.type, id: attachTo?.id, inspectionId },
            assetId,
            noteText: notes || undefined,
            deviceMetadata:
              geo.latitude && geo.longitude
                ? {
                    gps_latitude: geo.latitude,
                    gps_longitude: geo.longitude,
                    gps_accuracy: geo.accuracy || 0,
                  }
                : undefined,
          }),
        )
        evidence.isPending = false
        onCapture?.(evidence)
        setCaptureCount(c => c + 1)
        multiCapture ? handleRetake() : onClose?.()
      } catch (err) {
        if (import.meta.env.DEV) console.error('Upload failed, queuing offline:', err)
        await addToOfflineQueue(evidence, { attachTo, inspectionId })
        onCapture?.(evidence)
      } finally {
        setIsUploading(false)
      }
    } else {
      await addToOfflineQueue(evidence, { attachTo, inspectionId })
      onCapture?.(evidence)
      setCaptureCount(c => c + 1)
      multiCapture ? handleRetake() : onClose?.()
    }
  }, [capturedBlob, capturedImage, geo, audio, notes, isOnline, attachTo, inspectionId, onCapture, onClose, multiCapture, handleRetake])

  const handleAnnotationSave = useCallback((annotatedBlob: Blob) => {
    setCapturedImage(URL.createObjectURL(annotatedBlob))
    setCapturedBlob(annotatedBlob)
    setIsAnnotating(false)
  }, [])

  // Annotation overlay
  if (isAnnotating && capturedImage) {
    return <PhotoAnnotation imageUrl={capturedImage} onSave={handleAnnotationSave} onCancel={() => setIsAnnotating(false)} />
  }

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 bg-black/80 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LocationIndicator geo={geo} />
          <OnlineIndicator isOnline={isOnline} />
        </div>
        {multiCapture && captureCount > 0 && (
          <div className="px-2 py-1 rounded-full bg-emerald-600 text-white text-xs font-medium flex items-center gap-1">
            <Image className="w-3 h-3" />{captureCount}
          </div>
        )}
        <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
          <X className="w-5 h-5 text-white" />
        </button>
      </header>

      {/* Camera View / Preview */}
      <div className="flex-1 relative overflow-hidden">
        {cameraError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
            <CameraOff className="w-16 h-16 text-red-400 mb-4" />
            <p className="text-white text-lg font-medium mb-2">Camera Error</p>
            <p className="text-white/70 text-sm">{cameraError}</p>
          </div>
        ) : capturedImage ? (
          <img src={capturedImage} alt="Captured" className="absolute inset-0 w-full h-full object-contain" />
        ) : (
          <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Controls */}
      <footer className="flex-shrink-0 bg-black/80 px-4 py-4">
        {capturedImage ? (
          <PreviewControls
            notes={notes} onNotesChange={setNotes} audio={audio}
            isOnline={isOnline} isUploading={isUploading}
            onRetake={handleRetake} onAnnotate={() => setIsAnnotating(true)} onSave={handleSave}
          />
        ) : (
          <div className="flex items-center justify-center">
            <button
              onClick={handleCapture} disabled={!stream || !!cameraError}
              className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg active:scale-95 transition-transform disabled:opacity-50"
            >
              <div className="w-16 h-16 rounded-full border-4 border-slate-800" />
            </button>
          </div>
        )}
      </footer>
    </div>
  )
}
